import React, { useMemo } from "react";
import { Card, Statistic, Empty } from "antd";
import {
  AimOutlined,
  PercentageOutlined,
  ThunderboltOutlined,
  DollarOutlined,
  FieldTimeOutlined,
  NumberOutlined,
  BulbOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  LabelList,
} from "recharts";
import { BenchmarkRun, BenchmarkResult } from "@/lib/api/resources/benchmark";

// Treats a result as errored when the agent output carries an error payload.
const isErrored = (r: BenchmarkResult): boolean =>
  !!(r.actualOutput && (r.actualOutput as any).error);

const shortRef = (r: BenchmarkResult): string =>
  (
    r.inputReference?.split("/").pop() ||
    r.testCase?.name ||
    r.id ||
    ""
  ).toString();

const fmtMoney = (n: number | null | undefined): string =>
  n != null ? `$${n.toFixed(4)}` : "—";
const fmtNum = (n: number | null | undefined): string =>
  n != null ? n.toLocaleString() : "—";

interface DerivedStats {
  scored: BenchmarkResult[];
  passed: number;
  failed: number;
  errored: number;
  unscored: number;
  graded: number;
  verdictData: { name: string; value: number; color: string }[];
  scoreBuckets: { bucket: string; count: number }[];
  latencyBuckets: { bucket: string; count: number }[];
  topCost: { name: string; cost: number }[];
  slowest: BenchmarkResult | null;
  costliest: BenchmarkResult | null;
  poorlyJudged: BenchmarkResult[];
  hasJudge: boolean;
  hasCost: boolean;
  promptTokens: number;
  completionTokens: number;
}

const useDerivedStats = (results: BenchmarkResult[]): DerivedStats =>
  useMemo(() => {
    const errored = results.filter(isErrored);
    const completed = results.filter(
      (r) => !isErrored(r) && (r.latencyMs != null || r.actualOutput != null),
    );
    const passed = results.filter((r) => r.correct === true).length;
    const failed = results.filter((r) => r.correct === false).length;
    const unscored = completed.length - passed - failed;

    const verdictData = [
      { name: "Passed", value: passed, color: "#10b981" },
      { name: "Mismatch", value: failed, color: "#f59e0b" },
      { name: "Errored", value: errored.length, color: "#ef4444" },
      {
        name: "No Ground Truth",
        value: Math.max(unscored, 0),
        color: "#cbd5e1",
      },
    ].filter((d) => d.value > 0);

    // Judge score histogram, bucketed 0-1 .. 9-10.
    const scored = results.filter((r) => r.qualityScore != null);
    const buckets = Array.from({ length: 10 }, (_, i) => ({
      bucket: `${i}-${i + 1}`,
      count: 0,
    }));
    for (const r of scored) {
      const idx = Math.min(
        9,
        Math.max(0, Math.floor(r.qualityScore as number)),
      );
      buckets[idx].count += 1;
    }

    // Latency histogram with adaptive bucket size.
    const latencies = results
      .map((r) => r.latencyMs)
      .filter((v): v is number => v != null);
    let latencyBuckets: { bucket: string; count: number }[] = [];
    if (latencies.length > 0) {
      const max = Math.max(...latencies);
      const step = Math.max(100, Math.ceil(max / 6 / 100) * 100);
      const n = Math.max(1, Math.ceil((max + 1) / step));
      latencyBuckets = Array.from({ length: n }, (_, i) => ({
        bucket: `${(i * step) / 1000}-${((i + 1) * step) / 1000}s`,
        count: 0,
      }));
      for (const l of latencies) {
        const idx = Math.min(n - 1, Math.floor(l / step));
        latencyBuckets[idx].count += 1;
      }
    }

    const withCost = results.filter(
      (r) => r.cost != null && (r.cost as number) > 0,
    );
    const topCost = [...withCost]
      .sort((a, b) => (b.cost as number) - (a.cost as number))
      .slice(0, 6)
      .map((r) => ({
        name: shortRef(r),
        cost: Number((r.cost as number).toFixed(4)),
      }));

    const slowest = latencies.length
      ? results.reduce(
          (acc, r) =>
            r.latencyMs != null && (!acc || r.latencyMs > (acc.latencyMs ?? 0))
              ? r
              : acc,
          null as BenchmarkResult | null,
        )
      : null;
    const costliest = withCost.length
      ? withCost.reduce(
          (acc, r) =>
            !acc || (r.cost as number) > (acc.cost as number) ? r : acc,
          null as BenchmarkResult | null,
        )
      : null;

    const poorlyJudged = scored
      .filter((r) => (r.qualityScore as number) < 5)
      .sort((a, b) => (a.qualityScore as number) - (b.qualityScore as number));

    const promptTokens = results.reduce((s, r) => s + (r.promptTokens ?? 0), 0);
    const completionTokens = results.reduce(
      (s, r) => s + (r.completionTokens ?? 0),
      0,
    );

    return {
      scored,
      passed,
      failed,
      errored: errored.length,
      unscored: Math.max(unscored, 0),
      graded: passed + failed,
      verdictData,
      scoreBuckets: buckets,
      latencyBuckets,
      topCost,
      slowest,
      costliest,
      poorlyJudged,
      hasJudge: scored.length > 0,
      hasCost: withCost.length > 0,
      promptTokens,
      completionTokens,
    };
  }, [results]);

const KpiCard = ({
  title,
  value,
  suffix,
  prefix,
  color,
  hint,
  gradient,
}: {
  title: string;
  value: React.ReactNode;
  suffix?: string;
  prefix?: React.ReactNode;
  color: string;
  hint?: React.ReactNode;
  gradient: string;
}) => (
  <Card
    className={`shadow-sm border-gray-100 bg-gradient-to-br ${gradient}`}
    styles={{ body: { padding: 18 } }}
  >
    <Statistic
      title={title}
      value={value as any}
      suffix={suffix}
      prefix={prefix}
      styles={{ content: { color, fontWeight: 700, fontSize: 24 } }}
    />
    {hint && (
      <div className="text-gray-500 text-xs mt-2 leading-snug">{hint}</div>
    )}
  </Card>
);

const ChartCard = ({
  title,
  icon,
  children,
  empty,
  emptyDescription = "No data",
}: {
  title: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
  empty?: boolean;
  emptyDescription?: React.ReactNode;
}) => (
  <Card
    title={
      <span className="text-sm font-semibold">
        {icon} {title}
      </span>
    }
    className="shadow-sm border-gray-100 h-full"
    styles={{ body: { padding: 12 } }}
  >
    {empty ? (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={emptyDescription}
        className="my-8"
      />
    ) : (
      children
    )}
  </Card>
);

interface RunInsightsProps {
  run: BenchmarkRun;
  results: BenchmarkResult[];
}

export const RunInsights: React.FC<RunInsightsProps> = ({ run, results }) => {
  const s = useDerivedStats(results);

  const accuracy =
    run.accuracyRate != null
      ? run.accuracyRate * 100
      : s.graded > 0
        ? (s.passed / s.graded) * 100
        : null;
  const successRate =
    run.totalCases > 0
      ? ((run.completedCases - run.failedCases) / run.totalCases) * 100
      : null;

  // Auto-generated, human-readable insights derived from the run + per-case results.
  const insights: {
    icon: React.ReactNode;
    tone: string;
    text: React.ReactNode;
  }[] = [];

  if (accuracy != null) {
    const good = accuracy >= 80;
    insights.push({
      icon: good ? <CheckCircleOutlined /> : <WarningOutlined />,
      tone: good
        ? "text-emerald-700 bg-emerald-50 border-emerald-100"
        : "text-amber-700 bg-amber-50 border-amber-100",
      text: (
        <>
          Ground-truth accuracy is <strong>{accuracy.toFixed(1)}%</strong>{" "}
          across {s.graded} scored case
          {s.graded === 1 ? "" : "s"}
          {good
            ? " — solid performance."
            : " — there is room to improve the prompt or model."}
        </>
      ),
    });
  }

  if (s.hasJudge && run.averageQualityScore != null) {
    const good = run.averageQualityScore >= 7;
    insights.push({
      icon: <AimOutlined />,
      tone: good
        ? "text-emerald-700 bg-emerald-50 border-emerald-100"
        : "text-amber-700 bg-amber-50 border-amber-100",
      text: (
        <>
          The LLM judge rated outputs{" "}
          <strong>{run.averageQualityScore.toFixed(1)}/10</strong> on average
          {s.poorlyJudged.length > 0 ? (
            <>
              , flagging <strong>{s.poorlyJudged.length}</strong> case
              {s.poorlyJudged.length === 1 ? "" : "s"} below 5/10.
            </>
          ) : (
            " with no cases scored below 5/10."
          )}
        </>
      ),
    });
  }

  if (s.errored > 0) {
    insights.push({
      icon: <CloseCircleOutlined />,
      tone: "text-red-700 bg-red-50 border-red-100",
      text: (
        <>
          <strong>{s.errored}</strong> case{s.errored === 1 ? "" : "s"} errored
          out during execution
          {run.failureRate != null ? (
            <> ({Math.round(run.failureRate * 100)}% failure rate)</>
          ) : null}{" "}
          — check the Test Cases tab for details.
        </>
      ),
    });
  }

  if (s.slowest?.latencyMs != null) {
    insights.push({
      icon: <FieldTimeOutlined />,
      tone: "text-indigo-700 bg-indigo-50 border-indigo-100",
      text: (
        <>
          Slowest case was <strong>{shortRef(s.slowest)}</strong> at{" "}
          <strong>{s.slowest.latencyMs} ms</strong>
          {run.averageLatencyMs != null ? (
            <>
              {" "}
              vs a {run.averageLatencyMs} ms average
              {run.p95LatencyMs != null ? ` (P95 ${run.p95LatencyMs} ms)` : ""}.
            </>
          ) : (
            "."
          )}
        </>
      ),
    });
  }

  if (s.costliest?.cost != null) {
    insights.push({
      icon: <DollarOutlined />,
      tone: "text-purple-700 bg-purple-50 border-purple-100",
      text: (
        <>
          Most expensive case was <strong>{shortRef(s.costliest)}</strong> at{" "}
          <strong>{fmtMoney(s.costliest.cost)}</strong>
          {run.totalCost != null ? (
            <> of {fmtMoney(run.totalCost)} total.</>
          ) : (
            "."
          )}
        </>
      ),
    });
  }

  if (results.length === 0) {
    return (
      <Empty
        description="No results recorded for this run yet."
        className="my-16"
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {accuracy != null && (
          <KpiCard
            title="Accuracy"
            value={accuracy.toFixed(1)}
            suffix="%"
            prefix={<PercentageOutlined />}
            color="#059669"
            gradient="from-emerald-50 to-white"
            hint={`${s.passed}/${s.graded} ground-truth matches`}
          />
        )}
        {s.hasJudge && (
          <KpiCard
            title="Avg Judge Score"
            value={
              run.averageQualityScore != null
                ? run.averageQualityScore.toFixed(1)
                : "—"
            }
            suffix="/ 10"
            prefix={<AimOutlined />}
            color="#7c3aed"
            gradient="from-purple-50 to-white"
            hint="LLM-as-a-Judge"
          />
        )}
        {successRate != null && (
          <KpiCard
            title="Success Rate"
            value={successRate.toFixed(1)}
            suffix="%"
            prefix={<CheckCircleOutlined />}
            color="#2563eb"
            gradient="from-blue-50 to-white"
            hint={`${run.failedCases} of ${run.totalCases} errored`}
          />
        )}
        <KpiCard
          title="Avg Latency"
          value={run.averageLatencyMs != null ? run.averageLatencyMs : "—"}
          suffix={run.averageLatencyMs != null ? "ms" : ""}
          prefix={<ThunderboltOutlined />}
          color="#d97706"
          gradient="from-amber-50 to-white"
          hint={
            run.p95LatencyMs != null ? `P95 ${run.p95LatencyMs} ms` : undefined
          }
        />
        <KpiCard
          title="Total Tokens"
          value={fmtNum(run.totalTokens)}
          prefix={<NumberOutlined />}
          color="#0891b2"
          gradient="from-cyan-50 to-white"
          hint={
            s.promptTokens + s.completionTokens > 0
              ? `${fmtNum(s.promptTokens)} in · ${fmtNum(s.completionTokens)} out`
              : undefined
          }
        />
        <KpiCard
          title="Total Cost"
          value={run.totalCost != null ? run.totalCost.toFixed(4) : "—"}
          prefix={<DollarOutlined />}
          color="#16a34a"
          gradient="from-green-50 to-white"
          hint={
            s.hasCost
              ? `${fmtMoney((run.totalCost ?? 0) / Math.max(1, run.completedCases))} avg / case`
              : undefined
          }
        />
        <KpiCard
          title="Cases Run"
          value={run.completedCases}
          suffix={`/ ${run.totalCases}`}
          prefix={<FieldTimeOutlined />}
          color="#475569"
          gradient="from-slate-50 to-white"
          hint={`${s.passed} passed · ${s.failed} mismatch · ${s.errored} error`}
        />
        {s.hasJudge && (
          <KpiCard
            title="Poor (< 5/10)"
            value={s.poorlyJudged.length}
            prefix={<WarningOutlined />}
            color={s.poorlyJudged.length > 0 ? "#dc2626" : "#64748b"}
            gradient={
              s.poorlyJudged.length > 0
                ? "from-red-50 to-white"
                : "from-slate-50 to-white"
            }
            hint="Judge-flagged cases"
          />
        )}
      </div>

      {/* Insights narrative */}
      {insights.length > 0 && (
        <Card
          title={
            <span className="text-sm font-semibold">
              <BulbOutlined className="text-amber-500 mr-2" />
              Insights
            </span>
          }
          className="shadow-sm border-gray-100"
          styles={{ body: { padding: 16 } }}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {insights.map((ins, i) => (
              <div
                key={i}
                className={`flex items-start gap-2 text-sm border rounded-lg px-3 py-2 ${ins.tone}`}
              >
                <span className="mt-0.5">{ins.icon}</span>
                <span className="leading-snug">{ins.text}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ChartCard
          title="Ground Truth Match"
          icon={<CheckCircleOutlined className="text-emerald-500 mr-1" />}
          empty={s.verdictData.length === 0}
        >
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie
                data={s.verdictData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={55}
                outerRadius={85}
                paddingAngle={2}
              >
                {s.verdictData.map((d) => (
                  <Cell key={d.name} fill={d.color} />
                ))}
                <LabelList
                  dataKey="value"
                  position="outside"
                  className="text-xs"
                />
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex flex-wrap justify-center gap-3 -mt-2">
            {s.verdictData.map((d) => (
              <span
                key={d.name}
                className="flex items-center gap-1.5 text-xs text-gray-600"
              >
                <span
                  className="w-2.5 h-2.5 rounded-full inline-block"
                  style={{ backgroundColor: d.color }}
                />
                {d.name} ({d.value})
              </span>
            ))}
          </div>
        </ChartCard>

        <ChartCard
          title="Judge Score Distribution"
          icon={<AimOutlined className="text-purple-500 mr-1" />}
          empty={!s.hasJudge}
        >
          <ResponsiveContainer width="100%" height={240}>
            <BarChart
              data={s.scoreBuckets}
              margin={{ top: 16, right: 8, left: -16, bottom: 0 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                vertical={false}
                stroke="#f0f0f0"
              />
              <XAxis
                dataKey="bucket"
                tick={{ fontSize: 10 }}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                allowDecimals={false}
                tick={{ fontSize: 10 }}
                tickLine={false}
                axisLine={false}
              />
              <Tooltip cursor={{ fill: "#faf5ff" }} />
              <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                {s.scoreBuckets.map((b, i) => (
                  <Cell
                    key={b.bucket}
                    fill={i >= 8 ? "#10b981" : i >= 5 ? "#f59e0b" : "#ef4444"}
                  />
                ))}
                <LabelList
                  dataKey="count"
                  position="top"
                  className="text-[10px]"
                  formatter={(v: number) => (v > 0 ? v : "")}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="Latency Distribution"
          icon={<ThunderboltOutlined className="text-amber-500 mr-1" />}
          empty={s.latencyBuckets.length === 0}
        >
          <ResponsiveContainer width="100%" height={240}>
            <BarChart
              data={s.latencyBuckets}
              margin={{ top: 16, right: 8, left: -16, bottom: 0 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                vertical={false}
                stroke="#f0f0f0"
              />
              <XAxis
                dataKey="bucket"
                tick={{ fontSize: 10 }}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                allowDecimals={false}
                tick={{ fontSize: 10 }}
                tickLine={false}
                axisLine={false}
              />
              <Tooltip cursor={{ fill: "#eef2ff" }} />
              <Bar dataKey="count" fill="#6366f1" radius={[4, 4, 0, 0]}>
                <LabelList
                  dataKey="count"
                  position="top"
                  className="text-[10px]"
                  formatter={(v: number) => (v > 0 ? v : "")}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="Most Expensive Cases"
          icon={<DollarOutlined className="text-green-500 mr-1" />}
          empty={!s.hasCost}
          emptyDescription="No data (costs are not configured in the Benchmark configuration)"
        >
          <ResponsiveContainer width="100%" height={240}>
            <BarChart
              data={s.topCost}
              layout="vertical"
              margin={{ top: 4, right: 24, left: 8, bottom: 0 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                horizontal={false}
                stroke="#f0f0f0"
              />
              <XAxis
                type="number"
                tick={{ fontSize: 10 }}
                tickLine={false}
                axisLine={false}
                tickFormatter={(v) => `$${v}`}
              />
              <YAxis
                type="category"
                dataKey="name"
                width={90}
                tick={{ fontSize: 10 }}
                tickLine={false}
                axisLine={false}
              />
              <Tooltip
                cursor={{ fill: "#f0fdf4" }}
                formatter={(v: number) => [`$${v}`, "Cost"]}
              />
              <Bar dataKey="cost" fill="#22c55e" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </div>
  );
};

export default RunInsights;
